/**
 * Builds and fires the bounded operational event for an editor Remix parity divergence.
 *
 * Every attribute is drawn from a closed union, so the payload carries no flow identifier, no
 * content reference, and no user content. `remixId` scopes deduplication inside
 * `useEditorRemixParityDiagnostics` and deliberately never reaches this dispatcher.
 *
 * `treatmentPhase` records the migration phase the event was observed in. The editor runs in M2
 * shadow, where legacy state stays authoritative, so the only value today is `shadow`. It exists to
 * keep these events distinguishable once the editor read cutover (CCCEMAU-3488) lands. The renderer
 * equivalent reports `machine-read` because its cutover has already happened.
 *
 * EVENT IDENTITY: a distinct subject from the renderer's `rendererRemixStateParity`, rather than
 * emitting under that subject and discriminating on `entryPoint`. This mirrors the per-surface
 * pairing the renderer already established, so each surface owns both of its operational events:
 *
 *   renderer: `rendererRemixStateParity` + `rendererRemixLifecycleOperation`, source `rendererRemix`
 *   editor:   `editorRemixStateParity`   + `editorRemixLifecycleOperation`,   source `editorRemix`
 *
 * Because this is a new subject rather than a reuse, it needs its own Data Portal registration
 * before `remix_state_rearchitecture_editor` ramps — otherwise the dashboard stays empty, which is
 * indistinguishable from clean parity.
 */
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type {
	RemixParityDurationBucket,
	RemixParityOutcome,
	RemixParityReason,
} from './remixParityObservability.ts';
import type { EditorRemixDivergence, EditorRemixSevereDivergence } from './editorRemixParity.ts';

const ACTION = 'diverged';
const ACTION_SUBJECT = 'editorRemixStateParity';
const SOURCE = 'editorRemix';
const ENTRY_POINT = 'editor';
const TREATMENT_PHASE = 'shadow';
/** Shared with the renderer parity event so both surfaces can be queried by tag. */
const TAGS = ['remix', 'stateRearchitecture'];

export const createDivergenceSignature = ({
	producerKind,
	legacyPhase,
	machinePhase,
	comparisonPoint,
	divergenceReason,
}: EditorRemixDivergence): string =>
	`${producerKind}|${legacyPhase}|${machinePhase}|${comparisonPoint}|${divergenceReason}`;

export const dispatchEditorRemixParityEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		producerKind,
		legacyPhase,
		machinePhase,
		comparisonPoint,
		divergenceReason,
	}: EditorRemixSevereDivergence,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: ACTION,
				actionSubject: ACTION_SUBJECT,
				source: SOURCE,
				tags: TAGS,
				attributes: {
					entryPoint: ENTRY_POINT,
					severity: 'critical',
					outcome:
						divergenceReason === 'terminal-outcome-mismatch'
							? 'terminal-mismatch'
							: 'released-unresolved',
					reason: divergenceReason,
					producerKind,
					legacyPhase,
					machinePhase,
					comparisonPoint,
					divergenceReason,
					treatmentPhase: TREATMENT_PHASE,
				},
			},
		}).fire();
	} catch {
		// Diagnostics must never affect the editor flow.
	}
};

export type EditorRemixParityMechanic = Readonly<{
	producerKind: EditorRemixDivergence['producerKind'];
	legacyPhase: EditorRemixDivergence['legacyPhase'];
	machinePhase: EditorRemixDivergence['machinePhase'];
	comparisonPoint: EditorRemixDivergence['comparisonPoint'];
	action: 'observed' | 'resolved';
	outcome: Extract<RemixParityOutcome, 'timing-skew' | 'converged'>;
	reason: Extract<RemixParityReason, 'awaiting-convergence' | 'phase-converged'>;
	durationBucket: RemixParityDurationBucket;
}>;

/** Emits low-severity timing mechanics under a distinct dashboard subject. */
export const dispatchEditorRemixParityMechanicEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		producerKind,
		legacyPhase,
		machinePhase,
		comparisonPoint,
		action,
		outcome,
		reason,
		durationBucket,
	}: EditorRemixParityMechanic,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'editorRemixParityMechanic',
				source: SOURCE,
				tags: TAGS,
				attributes: {
					entryPoint: ENTRY_POINT,
					severity: 'info',
					outcome,
					reason,
					producerKind,
					legacyPhase,
					machinePhase,
					comparisonPoint,
					durationBucket,
					treatmentPhase: TREATMENT_PHASE,
				},
			},
		}).fire();
	} catch {
		// Diagnostics must never affect the editor flow.
	}
};
